import { useMemo, useCallback, useRef, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ModuleRegistry, AllCommunityModule } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import './ClientRequestsTable.css'
import type {
  ColDef,
  GridReadyEvent,
  GridApi,
  ColumnResizedEvent,
  DragStoppedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { isCrmCoreRole } from '@crm/shared'
import { useAuthStore } from '../auth/authStore'
import { AG_GRID_LOCALE_RU } from '../../shared/ag-grid/locale'
import { StatusEditor, STATUS_LABELS } from './StatusEditor'
import type { Request, RequestStatus } from '../../shared/api/types'
import { useGridSelection } from '../../shared/hooks/useGridSelection'

ModuleRegistry.registerModules([AllCommunityModule])

// Ключ по общей в проекте схеме `crm_<page>_cols_v{N}_${userId}` — версия
// позволяет при смене набора колонок сбросить старый кэш бампом N, а не
// глобальным localStorage.clear(). Прежний ключ `table-settings-${userId}`
// не читается → у текущих пользователей вид колонок один раз сбросится к
// дефолту (некритично — это под-таблица заявок клиента).
const COL_STATE_KEY = (userId: string) => `crm_client_requests_cols_v1_${userId}`

interface RowData {
  id: string
  requestNumber: string
  type: string
  typeRaw: string
  deadline: string | null
  status: RequestStatus
  itemsCount: number
  quotationSum: number | null
  quotationSumLabel: string
  orderedSum: number
}

function formatRub(n: number): string {
  return `${Math.round(n).toLocaleString('ru-RU')} ₽`
}

export function ClientRequestsTable({
  clientId,
  requests,
}: {
  clientId: string
  requests: Request[]
}) {
  const { user } = useAuthStore()
  const queryClient = useQueryClient()
  // Бухгалтер (этап 3, Б1) карточку клиента только смотрит: заявки ему закрыты,
  // поэтому номер заявки — текстом, статус — без редактора.
  const canOpenRequests = isCrmCoreRole(user?.role ?? '')

  const rowData = useMemo<RowData[]>(
    () =>
      requests.map((r) => {
        const quotationSum = r.quotationSnapshots?.[0]?.totalSum ?? null
        return {
          id: r.id,
          requestNumber: r.requestNumber ?? '—',
          type: r.type === 'TEKUCHKA' ? 'Текучка' : 'Проект',
          typeRaw: r.type,
          deadline: r.deadline ?? null,
          status: r.status,
          itemsCount: r._count?.items ?? 0,
          quotationSum,
          quotationSumLabel: quotationSum == null ? 'КП нет' : formatRub(quotationSum),
          orderedSum: r.orderedSum ?? 0,
        }
      }),
    [requests],
  )

  // Фильтры по статусу и типу + отфильтрованные строки и счётчик над таблицей.
  const [statusFilter, setStatusFilter] = useState<RequestStatus | ''>('')
  const [typeFilter, setTypeFilter] = useState<'' | 'TEKUCHKA' | 'PROJECT'>('')

  // Статусы для выпадашки — только те, что реально встречаются у заявок клиента.
  const statusOptions = useMemo(() => {
    const seen = new Set<RequestStatus>()
    for (const r of rowData) seen.add(r.status)
    return Array.from(seen)
  }, [rowData])

  const filteredRows = useMemo(
    () =>
      rowData.filter(
        (r) =>
          (!statusFilter || r.status === statusFilter) && (!typeFilter || r.typeRaw === typeFilter),
      ),
    [rowData, statusFilter, typeFilter],
  )

  const stats = useMemo(() => {
    let kpSum = 0
    let orderedSum = 0
    for (const r of filteredRows) {
      kpSum += r.quotationSum ?? 0
      orderedSum += r.orderedSum ?? 0
    }
    return { count: filteredRows.length, kpSum, orderedSum }
  }, [filteredRows])

  const filtersOn = !!statusFilter || !!typeFilter

  const onApplied = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['clients', clientId] })
  }, [queryClient, clientId])

  const colDefs = useMemo<ColDef<RowData>[]>(
    () => [
      {
        field: 'requestNumber',
        headerName: 'Заявка',
        minWidth: 110,
        cellRenderer: ({ data }: { data: RowData }) =>
          canOpenRequests ? (
            <Link
              to={`/requests/${data.id}`}
              className="text-brand-600 hover:text-brand-700 font-mono font-medium text-sm"
            >
              {data.requestNumber}
            </Link>
          ) : (
            <span className="font-mono font-medium text-sm text-gray-900">
              {data.requestNumber}
            </span>
          ),
      },
      {
        field: 'type',
        headerName: 'Тип',
        minWidth: 90,
      },
      {
        field: 'deadline',
        headerName: 'Дедлайн',
        minWidth: 100,
        valueFormatter: ({ value }: { value: string | null }) =>
          value ? new Date(value).toLocaleDateString('ru-RU') : '—',
      },
      {
        field: 'status',
        headerName: 'Статус',
        sortable: false,
        minWidth: 170,
        cellRenderer: ({ data }: { data: RowData }) =>
          canOpenRequests ? (
            <StatusEditor requestId={data.id} currentStatus={data.status} onApplied={onApplied} />
          ) : (
            <span className="text-sm text-gray-700">
              {STATUS_LABELS[data.status] ?? data.status}
            </span>
          ),
      },
      {
        field: 'itemsCount',
        headerName: 'Позиций',
        minWidth: 80,
      },
      {
        field: 'quotationSumLabel',
        headerName: 'Сумма КП',
        minWidth: 130,
        comparator: (_a: string, _b: string, nodeA, nodeB) => {
          const va = (nodeA?.data as RowData | undefined)?.quotationSum
          const vb = (nodeB?.data as RowData | undefined)?.quotationSum
          return (va ?? -1) - (vb ?? -1)
        },
        cellRenderer: ({ data }: { data: RowData }) =>
          data.quotationSum == null ? (
            <span className="text-gray-400 italic">КП нет</span>
          ) : (
            <span className="text-gray-900 font-medium">{data.quotationSumLabel}</span>
          ),
      },
    ],
    [onApplied, canOpenRequests],
  )

  const gridRef = useRef<AgGridReact>(null)
  const { onCellMouseDown, onCellMouseOver, getCellStyle } = useGridSelection(gridRef)

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      resizable: true,
      suppressMovable: false,
      cellStyle: getCellStyle,
    }),
    [getCellStyle],
  )

  const saveState = useCallback(
    (api: GridApi) => {
      if (!user?.id) return
      try {
        localStorage.setItem(COL_STATE_KEY(user.id), JSON.stringify(api.getColumnState()))
      } catch {
        /* ignore */
      }
    },
    [user?.id],
  )

  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      if (!user?.id) return
      const saved = localStorage.getItem(COL_STATE_KEY(user.id))
      if (saved) {
        try {
          params.api.applyColumnState({ state: JSON.parse(saved), applyOrder: true })
        } catch {
          /* ignore */
        }
      }
    },
    [user?.id],
  )

  return (
    <div className="space-y-2">
      {/* Панель фильтров + счётчик (кол-во · сумма КП · в заказ) */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as RequestStatus | '')}
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Все статусы</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABELS[s] ?? s}
            </option>
          ))}
        </select>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as '' | 'TEKUCHKA' | 'PROJECT')}
          className="px-2 py-1 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">Все типы</option>
          <option value="TEKUCHKA">Текучка</option>
          <option value="PROJECT">Проект</option>
        </select>
        {filtersOn && (
          <button
            onClick={() => {
              setStatusFilter('')
              setTypeFilter('')
            }}
            className="text-xs text-gray-400 hover:text-gray-600"
          >
            × Сбросить
          </button>
        )}
        <span className="ml-auto text-xs text-gray-500">
          {filtersOn ? 'найдено: ' : 'заявок: '}
          <span className="font-medium text-gray-900">{stats.count}</span>
          {' · '}сумма КП:{' '}
          <span className="font-medium text-gray-900">{formatRub(stats.kpSum)}</span>
          {' · '}в заказ:{' '}
          <span className="font-medium text-gray-900">{formatRub(stats.orderedSum)}</span>
        </span>
      </div>

      <div className="crm-table ag-theme-alpine rounded-xl overflow-x-auto border border-gray-200 bg-white shadow-sm">
        <AgGridReact
          ref={gridRef}
          getRowId={({ data }) => data.id}
          rowData={filteredRows}
          columnDefs={colDefs}
          defaultColDef={defaultColDef}
          localeText={AG_GRID_LOCALE_RU}
          onGridReady={onGridReady}
          onCellMouseDown={onCellMouseDown}
          onCellMouseOver={onCellMouseOver}
          onDragStopped={(e: DragStoppedEvent) => saveState(e.api)}
          onColumnResized={(e: ColumnResizedEvent) => {
            if (e.finished) saveState(e.api)
          }}
          onSortChanged={(e: SortChangedEvent) => saveState(e.api)}
          domLayout="autoHeight"
          rowHeight={40}
          headerHeight={40}
          suppressDragLeaveHidesColumns
          suppressCellFocus
          suppressRowHoverHighlight={false}
        />
      </div>
    </div>
  )
}
